import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Trophy, Home, LayoutDashboard, Users, LogOut, User, Settings, Swords, Menu, X } from 'lucide-react';
import { AuthContext } from '../AuthContext';

export default function Navigation() {
  const { user, isAuthenticated, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Close on escape key
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/riot-login');
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <nav className="main-nav">
        <Link to="/" className="nav-brand">
          <Trophy size={24} />
          <span>LoL Arena</span>
        </Link>

        {/* Desktop links */}
        <div className="nav-links">
          <Link to="/" className={isActive('/') ? 'nav-active' : ''}>
            <Home size={17} />
            <span className="nav-label">Accueil</span>
          </Link>
          <Link to="/tournaments" className={isActive('/tournaments') ? 'nav-active' : ''}>
            <Swords size={17} />
            <span className="nav-label">Arène</span>
          </Link>
          <Link to="/teams" className={isActive('/teams') ? 'nav-active' : ''}>
            <Users size={17} />
            <span className="nav-label">Équipes</span>
          </Link>

          {isAuthenticated ? (
            <>
              <Link to="/dashboard" className={isActive('/dashboard') ? 'nav-active' : ''}>
                <LayoutDashboard size={17} />
                <span className="nav-label">Tableau de Bord</span>
              </Link>
              {user?.role === 'admin' && (
                <Link to="/admin" className={`nav-admin-link${isActive('/admin') ? ' nav-active' : ''}`}>
                  <Settings size={17} />
                  <span className="nav-label">Admin</span>
                </Link>
              )}
              <div className="user-menu">
                <Link to="/profile" className={`user-name${isActive('/profile') ? ' nav-active' : ''}`}>
                  <User size={17} />
                  <span className="nav-label">
                    {user?.displayName || user?.username}
                    {user?.needsEmail && (
                      <span className="nav-email-badge" title="Ajoutez votre email">!</span>
                    )}
                  </span>
                </Link>
                <button onClick={handleLogout} className="logout-link">
                  <LogOut size={17} />
                  <span className="nav-label">Déconnexion</span>
                </button>
              </div>
            </>
          ) : (
            <Link to="/riot-login" className="register-button">
              <User size={17} />
              <span className="nav-label">Se connecter</span>
            </Link>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className="nav-hamburger"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="nav-mobile-overlay" onClick={() => setMobileOpen(false)}>
          <nav className="nav-mobile-panel" onClick={e => e.stopPropagation()}>
            <Link to="/" className={isActive('/') ? 'nav-mobile-active' : ''}>
              <Home size={18} /> Accueil
            </Link>
            <Link to="/tournaments" className={isActive('/tournaments') ? 'nav-mobile-active' : ''}>
              <Swords size={18} /> Arène
            </Link>
            <Link to="/teams" className={isActive('/teams') ? 'nav-mobile-active' : ''}>
              <Users size={18} /> Équipes
            </Link>

            {isAuthenticated ? (
              <>
                <div className="nav-mobile-divider" />
                <Link to="/dashboard" className={isActive('/dashboard') ? 'nav-mobile-active' : ''}>
                  <LayoutDashboard size={18} /> Tableau de Bord
                </Link>
                <Link to="/profile" className={isActive('/profile') ? 'nav-mobile-active' : ''}>
                  <User size={18} /> Profil
                </Link>
                {user?.role === 'admin' && (
                  <Link to="/admin" className={isActive('/admin') ? 'nav-mobile-active' : ''}>
                    <Settings size={18} /> Admin
                  </Link>
                )}
                <div className="nav-mobile-divider" />
                <button onClick={handleLogout} className="nav-mobile-logout">
                  <LogOut size={18} /> Déconnexion
                </button>
              </>
            ) : (
              <>
                <div className="nav-mobile-divider" />
                <Link to="/riot-login" className="nav-mobile-cta">
                  <User size={18} /> Se connecter
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </>
  );
}
